import React, { useEffect, useState } from "react";
import Icon from "../../UI/Icon";
import { Badge, Button, Skeleton } from "../../UI/Primitives";
import { exGst, formatINR } from "../../../Service/catalog";

// Tenure choices offered by the storefront (unchanged from the previous slider).
export const RENT_PERIODS = [6, 9, 12];

function useTick(enabled, ms) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => setTick((t) => t + 1), ms);
    return () => clearInterval(timer);
  }, [enabled, ms]);
}

/**
 * Rental configuration + CTAs. All pricing comes from ProductComponent's
 * existing showPrice() logic over the inventory rentOption list.
 */
function RentalPanel({
  product,
  inventory,
  durationSelected,
  onSelectDuration,
  quantity,
  onQuantity,
  showPrice,
  onRentNow,
  onAddToCart,
  busy,
  added,
  notice,
  isExpress,
  expressSlot,
  cityLabel,
  onChangeCity,
  onShare,
  shareLabel,
}) {
  const slug = product.category ? product.category.slug : "";
  const isMedical = product.isMedical === true;
  const isPremium = slug === "premium";
  const isSeasonal = slug === "seasonal";
  const outOfStock = product.status === "Out of Stock";
  const rentOptions = (inventory && inventory.rentOption) || [];
  const inventoryReady = rentOptions.length > 0;
  const periods = RENT_PERIODS.filter((p) => rentOptions.some((r) => Number(r.period) === p));
  const priceFor = (period) => {
    const option = rentOptions.find((r) => Number(r.period) === period);
    return option ? exGst(option.price) : null;
  };
  const offered = periods.map(priceFor).filter((value) => value !== null);
  const lowest = offered.length > 1 ? Math.min(...offered) : null;

  useTick(isExpress, 30000);
  const express = isExpress ? expressSlot() : null;

  const single = exGst(showPrice("single"));
  const monthlyTotal = exGst(showPrice("final"));
  const deposit = Math.round(Number(showPrice("final")) || 0);
  const unit = isSeasonal ? "season" : "month";
  const showTenurePicker = !isMedical && !isPremium && !isSeasonal;
  const minTenure = isPremium ? "12 months" : isMedical ? null : isSeasonal ? null : "6 months";

  return (
    <div className="v-rent">
      {isMedical ? (
        <div className="v-rent__quote">
          <p className="v-rent__quote-title">Pricing on request</p>
          <ul>
            <li>You can add the product to cart and checkout in next stage.</li>
            <li>We will connect with you shortly with pricing and delivery information.</li>
          </ul>
        </div>
      ) : (
        <div className="v-rent__price" aria-live="polite">
          {inventoryReady ? (
            <>
              <span className="v-rent__amount">{formatINR(single)}</span>
              <span className="v-rent__unit">/{unit}</span>
              {isPremium ? <Badge tone="brand">12-month plan</Badge> : null}
            </>
          ) : (
            <Skeleton width="160px" height="40px" />
          )}
          <p className="v-rent__tax">Price excludes GST</p>
        </div>
      )}

      {showTenurePicker ? (
        <fieldset className="v-rent__group">
          <legend className="v-rent__label">Choose tenure</legend>
          {inventoryReady ? (
            <div className="v-tenure" role="radiogroup" aria-label="Rental tenure">
              {(periods.length ? periods : RENT_PERIODS).map((period) => {
                const price = priceFor(period);
                const selected = Number(durationSelected) === period;
                return (
                  <button
                    key={period}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`v-tenure__opt ${selected ? "is-active" : ""}`}
                    onClick={() => onSelectDuration(period)}
                  >
                    <span className="v-tenure__months">{period} months</span>
                    {price ? <span className="v-tenure__price">{formatINR(price)}/mo</span> : null}
                    {lowest !== null && price === lowest ? <span className="v-tenure__best">Lowest rent</span> : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="v-tenure">
              {RENT_PERIODS.map((p) => (
                <Skeleton key={p} height="64px" radius="var(--radius-md)" />
              ))}
            </div>
          )}
        </fieldset>
      ) : null}

      <div className="v-rent__row">
        <span className="v-rent__label">Quantity</span>
        <div className="v-qty" role="group" aria-label="Quantity">
          <button type="button" className="v-qty__btn" onClick={() => onQuantity("-")} disabled={quantity <= 1} aria-label="Decrease quantity">
            <Icon name="minus" size={16} />
          </button>
          <output className="v-qty__value" aria-live="polite">{quantity}</output>
          <button type="button" className="v-qty__btn" onClick={() => onQuantity("+")} aria-label="Increase quantity">
            <Icon name="plus" size={16} />
          </button>
        </div>
      </div>

      {notice ? (
        <p className={`v-rent__notice v-rent__notice--${notice.tone || "info"}`} role="alert">
          <Icon name={notice.tone === "error" ? "alert" : "info"} size={16} />
          {notice.text}
        </p>
      ) : null}

      {!isMedical && inventoryReady ? (
        <dl className="v-rent__summary">
          {quantity > 1 || isPremium ? (
            <div>
              <dt>Monthly total</dt>
              <dd>{formatINR(monthlyTotal)}/{unit === "season" ? "season" : "mo"}</dd>
            </div>
          ) : null}
          {isPremium ? (
            <>
              <div>
                <dt>Tenure</dt>
                <dd>12 months</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>{formatINR(monthlyTotal * 12)}</dd>
              </div>
            </>
          ) : null}
          {isSeasonal ? (
            <div>
              <dt>Total</dt>
              <dd>{formatINR(monthlyTotal)}</dd>
            </div>
          ) : null}
          <div>
            <dt>Security deposit</dt>
            <dd>{isSeasonal ? "None" : `${formatINR(deposit)} · refundable`}</dd>
          </div>
        </dl>
      ) : null}

      <div className="v-rent__ctas">
        <Button variant="primary" size="lg" block onClick={onRentNow} disabled={outOfStock || busy} arrow={!busy}>
          {busy === "rent" ? <span className="v-spinner" aria-hidden="true" /> : null}
          {outOfStock ? "Currently out of stock" : busy === "rent" ? "Reserving…" : "Rent now"}
        </Button>
        {added ? (
          <Button variant="secondary" size="lg" block href="/cart" icon="bag">
            View cart
          </Button>
        ) : (
          <Button variant="secondary" size="lg" block onClick={onAddToCart} disabled={outOfStock || busy} icon={busy === "cart" ? null : "bag"}>
            {busy === "cart" ? <span className="v-spinner" aria-hidden="true" /> : null}
            {busy === "cart" ? "Adding…" : "Add to cart"}
          </Button>
        )}
        {added ? (
          <p className="v-rent__added" role="status">
            <Icon name="check" size={16} /> Added to your cart.{" "}
            <button type="button" className="v-link v-link--button" onClick={onAddToCart}>
              Update cart
            </button>
          </p>
        ) : null}
        {product.isVirtual === true ? (
          <Button
            variant="tonal"
            block
            href={
              product.tagName && product.tagName.includes("VRBedroom")
                ? "/vrBedroom"
                : "/vrKitchen"
            }
          >
            View in virtual
          </Button>
        ) : null}
      </div>

      <ul className="v-rent__facts">
        {express ? (
          <li className="v-rent__fact v-rent__fact--express">
            <Icon name="bolt" size={18} />
            <span>
              <strong>{express.label} · {express.deliverySlot.trim()}</strong>
              <span>Order within {express.countdown.replace(/ \d+s$/, "")}</span>
            </span>
          </li>
        ) : (
          <li className="v-rent__fact">
            <Icon name="truck" size={18} />
            <span>
              <strong>Free delivery in 2–3 days</strong>
              <span>Installation included</span>
            </span>
          </li>
        )}
        {minTenure ? (
          <li className="v-rent__fact">
            <Icon name="calendar" size={18} />
            <span>
              <strong>Minimum rental {minTenure}</strong>
              <span>Extends automatically · close with 2 weeks’ notice</span>
            </span>
          </li>
        ) : null}
        <li className="v-rent__fact">
          <Icon name="pin" size={18} />
          <span>
            <strong>Delivering in {cityLabel}</strong>
            <button type="button" className="v-link v-link--button" onClick={onChangeCity}>
              Change city
            </button>
          </span>
        </li>
      </ul>

      <button type="button" className="v-rent__share" onClick={onShare}>
        <Icon name="share" size={16} /> {shareLabel}
      </button>
    </div>
  );
}

export default RentalPanel;
